'use client';
import Image from 'next/image';

import { useEditableDotCMSPage } from '@dotcms/react';
import LinkNoPrefetch from '@/components/link-noprefetch';

import Badge from '@/components/shared/badge';
import Tag from '@/components/shared/tag';
import ListingContentlets from '@/components/shared/listingContentlets';
import { CollectionCard } from '@/components/content-types/collection-type/components/collectionCard';
import { DotBlockEditor } from '@/components/shared/decodeTextBlock';
import Blog from '@/components/block-editor';
import customRenderers from '@/components/block-editor';
import { isBlockEditorEmpty } from '@/app/utils';

const ListItem = (props) => {
    const text = props.content[0].content[0].text;
    return (
        <Tag bgColor="bg-blue-100" fontColor="text-blue">
            <span className="flex gap-2 px-4 py-2">
                <Image
                    src={'/assets/icons/check.svg'}
                    width={16}
                    height={16}
                    alt="check"
                    unoptimized
                />
                {text}
            </span>
        </Tag>
    );
};

const OrderedList = ({ content }) => {
    return (
        <ol className="flex flex-col gap-4 text-base">
            {content.map((item, i) => {
                return <ListItem key={i} {...item} />;
            })}
        </ol>
    );
};

const CaseStudyCard = ({ data }) => {
    return (
        <div className="my-6 flex flex-col gap-4">
            <LinkNoPrefetch
                href={`/case-study/${data?.urlTitle}`}
                className="flex flex-col overflow-hidden rounded-lg bg-white no-underline shadow-md transition-shadow duration-300 hover:shadow-lg sm:flex-row">
                <div className="relative h-48 w-full sm:h-auto sm:w-1/3">
                    <Image
                        src={data?.image}
                        alt={data?.title}
                        quality={50}
                        layout="fill"
                        objectFit="cover"
                    />
                </div>
                <div className="w-full p-6 sm:w-2/3">
                    <h3 className="mb-2 mt-0 text-xl font-semibold">{data?.heading}</h3>
                    <p className="mb-0 text-custom-mystic">{data?.subheading}</p>
                </div>
            </LinkNoPrefetch>
        </div>
    );
};

function ContentRenderer({ ...props }) {
    const data = props?.attrs?.data;

    const Component = customRenderers?.[data?.contentType];

    if (!Component) {
        return <CaseStudyCard data={data} />;
    }

    return <Component {...props} />;
}

export function PartnersDetail({ pageResponse, relatedContent }) {
    // The hook returns undefined until UVE resolves a draft/non-live page, so read defensively.
    const pageAsset = useEditableDotCMSPage(pageResponse)?.pageAsset;

    // Use data from pageAsset if available, otherwise fall back to prop
    const partnerData = pageAsset?.urlContentMap;

    const website = partnerData?.website1?.startsWith('http')
        ? partnerData?.website1
        : `https://${partnerData?.website1}`;

    return (
        <div className="mb-24 space-y-20">
            <div className="background-partners-detail-banner h-[clamp(230px,58vw,540px)] px-[20%]">
                {partnerData?.identifier && (
                    <div className="relative mx-auto flex size-full max-w-4xl items-center justify-center px-2">
                        <Image
                            priority
                            src={partnerData.identifier}
                            width={442}
                            height={436}
                            quality={50}
                            sizes="(max-width: 442px) calc(100vw - 32px), (max-width: 768px) calc(100vw - 40%), 442px"
                            className="object-contain"
                            alt={`Partner ${partnerData.title} logo`}
                        />
                    </div>
                )}
            </div>

            <div className="container">
                <div className="mx-auto max-w-full md:max-w-4xl">
                    <h2 className="mb-2 h-auto text-base">
                        <Badge className="inline-block w-fit !bg-fuschia-200 !text-fuschia-650">
                            About
                        </Badge>
                    </h2>
                    {!isBlockEditorEmpty(partnerData?.overview) && (
                        <DotBlockEditor
                            className="prose max-w-full p-0"
                            blocks={partnerData.overview}
                            customRenderers={{
                                Blog,
                                dotContent: ContentRenderer,
                                orderedList: OrderedList
                            }}
                        />
                    )}
                </div>
            </div>

            <div className="border-y border-indigo-300">
                <div className="container flex flex-wrap gap-x-8 gap-y-4 py-4">
                    {!!partnerData?.website1 && (
                        <dl className="flex items-center gap-2">
                            <dt className="font-medium">Website:</dt>
                            <dd className="text-fuschia">
                                <a
                                    href={website}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-sm text-fuschia">
                                    {partnerData.website1}
                                </a>
                            </dd>
                        </dl>
                    )}

                    {!!partnerData?.email && (
                        <dl className="flex items-center gap-2">
                            <dt className="font-medium">Email:</dt>
                            <dd className="text-fuschia">
                                <a
                                    href={`mailto:${partnerData.email}`}
                                    className="text-sm text-fuschia">
                                    {partnerData.email}
                                </a>
                            </dd>
                        </dl>
                    )}

                    {!!partnerData?.phone && (
                        <dl className="flex items-center gap-2">
                            <dt className="font-medium">Phone:</dt>
                            <dd className="text-fuschia">
                                <a
                                    href={`tel:${partnerData.phone}`}
                                    className="text-sm text-fuschia">
                                    {partnerData.phone}
                                </a>
                            </dd>
                        </dl>
                    )}

                    {!!partnerData?.address && (
                        <dl className="flex items-center gap-2">
                            <dt className="font-medium">Location:</dt>
                            <dd className="text-fuschia">
                                <address>
                                    <DotBlockEditor
                                        blocks={partnerData.address}
                                        className="text-sm not-italic"
                                        customRenderers={{
                                            paragraph: ({ children }) => <> {children} </>
                                        }}
                                    />
                                </address>
                            </dd>
                        </dl>
                    )}
                </div>
            </div>

            {!!relatedContent.length && (
                <div className="container">
                    <h2 className="mb-4 text-xl font-semibold">Related Resources</h2>
                    <ListingContentlets
                        Card={({ contentlet, ...props }) => (
                            <CollectionCard
                                contentlet={contentlet}
                                contenttype={contentlet.contentType}
                                showCategory={true}
                                {...props}
                            />
                        )}
                        contentlets={relatedContent}
                    />
                </div>
            )}
        </div>
    );
}
